import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import SelectBox from 'devextreme-react/select-box';
import CheckBox from 'devextreme-react/check-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Timeline, TimelineOptions, DataItem, DataGroup, DataSet } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import { TIMELINE, STATUS } from '../shared/Utils';
import { DashboardV2Dto } from '../../dtos/DashboardV2Dto';
import { DashboardProjectDto } from '../../dtos/DashboardProjectDto';
import { DashboardEventDto } from '../../dtos/DashboardEventDto';
import dashboardApi from '../../api/DashboardApi.js';
import { getDashboardTimeline } from '../../api/v2/DashboardApi';
import EventTooltip from '../shared/timeline/EventTooltip';
import StatusLegend from '../grantDetails/progressTimeline/shared/StatusLegend';
import { eventTriangleHtml, tickKind } from '../shared/timeline/eventTicks';
import { HoverTip, HoverTipDay, TooltipEvent, mergeTicksToDays } from '../shared/timeline/eventTooltipTypes';
import { useAppContext } from '../shared/AppContext';
import { buildDeepLinkRoute } from '../shared/tasklist/deepLinkUtils';
import { downloadFileById } from '../shared/fileDownload';
import '../css/DashboardTimeline.css';

// The dashboard plots every project's phases as consecutive bars measured in months from the
// project's own start, with all rows left-aligned — the same relative-months layout the legacy
// Google Charts timeline used. vis-timeline works in real dates, so we place those month offsets on
// an axis anchored at a fixed reference date (EPOCH) and label each tick with the month number
// instead of a calendar date. The backend returns each phase's duration and status, plus the
// files/comments/notes on that phase, which we draw as event ticks on the bars (like the Progress
// Monitor). An event's real date is mapped into its phase's calendar span and then onto the bar.
const EPOCH = new Date(2000, 0, 1);
const monthsToDate = (m: number): Date => moment(EPOCH).add(m, 'months').toDate();
// moment's add(fractionalMonths) rounds to whole months (add(0.4) -> 0 days, add(0.5) -> a full
// month), so a sub-month width can't be expressed that way. Offsetting a month-anchored date by a
// day count instead gives a true, continuous width — used to draw the current-phase stub thin.
const addDaysToDate = (d: Date, days: number): Date => moment(d).add(days, 'days').toDate();
const monthsSinceEpoch = (d: Date): number => Math.round(moment(d).diff(moment(EPOCH), 'months', true));

const escapeHtml = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const stripHtml = (s: string): string => s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const statusColor = (status: string): string => {
    switch (status) {
        case STATUS.COMPLETED: return TIMELINE.COMPLETEDCOLOR;
        case STATUS.INPROGRESS: return TIMELINE.INPROGRESSCOLOR;
        case STATUS.SLIGHTLYLATE: return TIMELINE.SLIGHTLYLATECOLOR;
        case STATUS.LATE: return TIMELINE.LATECOLOR;
        default: return TIMELINE.SCHEDULEDCOLOR;
    }
};

// Switch for the label drawn on each bar. On: the task's name. Off: its short abbreviation.
const SHOW_TASK_NAMES_ON_BARS = true;
// Switch for events on tasks with no schedule dates, like the Progress Monitor's Undated button.
// Off: they are left off the chart. On: they sit on the bar whose dates cover them.
const SHOW_UNDATED_EVENTS = false;
const TODAY_MARKER_ID = 'today';

// Render width (in days) for a phase that has started but whose duration rounds to under a month —
// the "current phase" stub. Matches the legacy chart's min-width sliver for a reached phase. Drawn in
// days (not fractional months) so it's a true thin sliver rather than snapping to a whole month.
const STUB_DAYS = 8;

// A project is "on track" when every drawn phase is Completed (green) — the on-track filter hides
// these so users can focus on projects with active or behind-schedule work.
const isProjectOnTrack = (project: DashboardProjectDto): boolean =>
    project.phases.length > 0 && project.phases.every(ph => ph.status === STATUS.COMPLETED);

const toTooltipEvent = (ev: DashboardEventDto, resubPhaseNames: Set<string>, grantId: string): TooltipEvent => ({
    type: ev.type,
    label: ev.type === 'File'
        ? (ev.fileName ?? '')
        : (ev.commentText ? stripHtml(ev.commentText) : ev.type),
    commentType: ev.commentType,
    fileId: ev.fileId,
    fileName: ev.fileName,
    phaseName: ev.phaseName,
    stepName: ev.step ?? '',
    userName: ev.author ?? '',
    resubmission: ev.resubmission ?? null,
    phaseHasResubmissions: resubPhaseNames.has(ev.phaseName),
    // Location for the tooltip's "open" deep links.
    grantId,
    moduleTypeId: ev.moduleTypeId,
    phaseId: ev.phaseId,
    stepId: ev.stepId,
});

interface BuiltTimeline {
    groups: DataGroup[];
    items: DataItem[];
    // Months from the reference start (project pipeline start) to today — where the current-date line goes.
    monthsToToday: number;
    axisMonths: number;
    // Months labeled on the axis: 0 and each drawn phase's planned end, from the phase templates.
    labelMonths: number[];
}

// Turn the v2 payload into vis groups (one per project) and items (a bar per phase, a triangle per
// day of events). hideOnTrack drops projects whose phases are all Completed.
const buildTimeline = (data: DashboardV2Dto | null, hideOnTrack: boolean): BuiltTimeline => {
    const projects: DashboardProjectDto[] = data?.projects ?? [];
    const groups: DataGroup[] = [];
    const items: DataItem[] = [];
    let maxEnd = 0;

    projects.forEach(project => {
        if (hideOnTrack && isProjectOnTrack(project)) return;

        const grantId = String(project.grantId);
        const label = escapeHtml(project.institution ?? '');
        const fullLabel = escapeHtml(`${project.institution} (${project.grantNumber})`);
        groups.push({
            id: grantId,
            content: `<span class="db-rowlabel" title="${fullLabel}">${label}</span>`,
        } as DataGroup);

        // Bars stack left to right by duration. Remember each drawn bar's offset + calendar span so
        // events can be placed proportionally along the bar that covers their date. A phase can
        // collapse to zero months (its calendar span was fully consumed by an earlier phase, or it
        // was merged) and then draws no bar — but its documents still need a home, so we place them
        // on whichever drawn bar's calendar span covers the event date.
        let cursor = 0;
        const drawnBars: { phaseName: string; barStart: number; duration: number; actualStart: Date | null; actualEnd: Date | null }[] = [];

        project.phases.forEach((ph, idx) => {
            const months = ph.durationMonths ?? 0;
            // A phase whose duration rounds to under a month still draws as a thin stub — this is the
            // in-progress "current" phase (e.g. DD just started). The backend only emits phases that
            // have actually started (Scheduled phases are filtered out), so every phase here should
            // draw; the legacy Google Charts dashboard likewise drew a min-width sliver for a reached,
            // zero-length phase. The stub renders STUB_DAYS wide but advances the cursor by 0, so it
            // never shifts later phases (legacy drew these with start == end).
            const isStub = months <= 0;
            const start = cursor;
            const end = start + months;
            cursor = start + Math.max(0, months);
            if (end > maxEnd) maxEnd = end;

            const startDate = monthsToDate(start);
            const endDate = isStub ? addDaysToDate(startDate, STUB_DAYS) : monthsToDate(end);
            const color = statusColor(ph.status);
            items.push({
                id: `${grantId}-bar-${idx}`,
                group: grantId,
                content: SHOW_TASK_NAMES_ON_BARS ? ph.phaseName : ph.phaseAbbrev,
                start: startDate,
                end: endDate,
                type: 'range',
                // Stub bars get an extra class so the label sits just past their right edge (like legacy),
                // rather than overlapping the sliver.
                className: isStub ? 'db-bar db-bar-stub' : 'db-bar',
                title: `<strong>${ph.phaseName}</strong><br/>${ph.status}<br/>Duration: ${months} month${months === 1 ? '' : 's'}`,
                style: `background-color: ${color}; border-color: ${color}; color: #000;`,
            } as DataItem);

            drawnBars.push({
                phaseName: ph.phaseName,
                barStart: start,
                duration: months,
                actualStart: ph.actualStart ? new Date(ph.actualStart) : null,
                actualEnd: ph.actualEnd ? new Date(ph.actualEnd) : null,
            });
        });

        // Pick the drawn bar an event sits on: its own phase's bar when that was drawn, otherwise the
        // bar whose real calendar span contains the event date, otherwise the nearest bar by date.
        // This keeps documents from disappearing when their phase collapsed to a zero-width bar.
        const barForEvent = (phaseName: string, date: Date) => {
            const own = drawnBars.find(b => b.phaseName === phaseName);
            if (own) return own;
            if (drawnBars.length === 0) return null;
            const containing = drawnBars.find(b => b.actualStart && b.actualEnd && date >= b.actualStart && date <= b.actualEnd);
            if (containing) return containing;
            // Nearest by calendar: last bar that starts on or before the date, else the first bar.
            let nearest = drawnBars[0];
            for (const b of drawnBars) {
                if (b.actualStart && b.actualStart <= date) nearest = b;
            }
            return nearest;
        };

        // Phases (of this grant) that have any resubmission file — decided across all the grant's
        // events so every file in such a phase is tagged, whatever date/tick it lands on.
        const resubPhaseNames = new Set(
            (project.events ?? [])
                .filter(ev => ev.type === 'File' && ev.resubmission != null)
                .map(ev => ev.phaseName),
        );

        // Group events by phase + calendar day so a busy day reads as one triangle with a count.
        const clusters = new Map<string, { offset: number; date: Date; phaseName: string; events: DashboardEventDto[] }>();
        (project.events ?? []).forEach(ev => {
            const date = new Date(ev.date);
            if (isNaN(date.getTime()) || date.getFullYear() < 1900) return;
            if (ev.undated && !SHOW_UNDATED_EVENTS) return;
            const bar = barForEvent(ev.phaseName, date);
            if (!bar) return;

            // Map the real date into the chosen bar's calendar span, then onto the duration-based bar.
            const elapsed = bar.actualStart ? moment(date).diff(moment(bar.actualStart), 'months', true) : 0;
            const offset = bar.barStart + Math.max(0, Math.min(bar.duration, elapsed));

            const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            const key = `${grantId}|${ev.phaseName}|${dayKey}`;
            let cluster = clusters.get(key);
            if (!cluster) {
                cluster = { offset, date, phaseName: ev.phaseName, events: [] };
                clusters.set(key, cluster);
            }
            cluster.events.push(ev);
        });

        clusters.forEach((cluster, key) => {
            const fileCount = cluster.events.filter(e => e.type === 'File').length;
            const noteCount = cluster.events.filter(e => e.type === 'Note').length;
            const commentCount = cluster.events.filter(e => e.type === 'Comment').length;
            items.push({
                id: `ev-${key}`,
                group: grantId,
                start: monthsToDate(cluster.offset),
                // 'box' (center-aligned) rather than 'point' so an edge tick stays visible while any
                // part of it is in the window, matching the Progress Monitor's ticks.
                type: 'box',
                className: 'db-event-tick',
                content: eventTriangleHtml(cluster.events.length, tickKind(noteCount, fileCount + commentCount)),
                // No native title: the interactive React tooltip is driven by these pt* fields. The
                // *Count/tipDate fields let vis's cluster bubble sum its merged day-ticks (see
                // patchClusterCounts) and sort them chronologically in the tooltip.
                eventCount: cluster.events.length,
                fileCount,
                commentCount,
                noteCount,
                tipDate: cluster.date.getTime(),
                // Empty header: the tooltip runs at 'modules' depth, so phase (and step) show as
                // section headings inside the body. A boundary cluster can merge ticks from two
                // phases, so a single header phase would be wrong — the per-section phase is correct.
                ptHeader: '',
                ptDateStr: moment(cluster.date).format('MM-DD-YYYY'),
                ptEvents: cluster.events.map(ev => toTooltipEvent(ev, resubPhaseNames, grantId)),
            } as DataItem & {
                eventCount: number; fileCount: number; commentCount: number; noteCount: number;
                tipDate: number; ptHeader: string; ptDateStr: string; ptEvents: TooltipEvent[];
            });
        });
    });

    const refStart = data?.startDate ? new Date(data.startDate) : null;
    const monthsToToday = refStart
        ? Math.max(0, Math.round(moment().diff(moment(refStart), 'months', true)))
        : 0;

    // The axis is never narrower than the last planned phase end, and grows if a project runs longer.
    const labelMonths = data?.axisMonths ?? [];
    const plannedMonths = labelMonths.length > 0 ? Math.max(...labelMonths) : 0;
    const axisMonths = Math.max(plannedMonths, maxEnd, monthsToToday) + 1;
    return { groups, items, monthsToToday, axisMonths, labelMonths };
};

// Let vis collapse overlapping event ticks (box items) into a single count bubble. Zoom is disabled
// on the dashboard, so these stay collapsed at the fixed scale — the count bubble plus the hover
// tooltip carry the detail. Only ticks cluster (never the range bars).
const CLUSTER_OPTION = {
    maxItems: 1,
    showStipes: false,
    fitOnDoubleClick: true,
    clusterCriteria: (a: any, b: any) => a?.type === 'box' && b?.type === 'box',
};

// vis stashes its ItemSet under a few different names depending on build; resolve it once.
const getItemSet = (tl: any) => tl?.itemSet || tl?._itemSet || tl?.body?.itemSet;

// vis renders a collapsed cluster's number as the count of sub-items it merged (here, day-ticks). We
// want the actual number of events, so rewrite each cluster's content to the sum of its ticks'
// eventCount. This only touches the rendered DOM (no vis redraw), so it's safe from 'changed'.
const patchClusterCounts = (tl: any) => {
    if (!tl) return;
    const clusters: any[] = getItemSet(tl)?.clusters;
    if (!clusters?.length) return;
    clusters.forEach(cluster => {
        const uiItems: any[] = cluster?.data?.uiItems ?? [];
        let total = 0;
        let noteTotal = 0;
        let otherTotal = 0;
        uiItems.forEach(ui => {
            const d = ui?.data;
            total += d?.eventCount ?? 1;
            noteTotal += d?.noteCount ?? 0;
            otherTotal += (d?.fileCount ?? 0) + (d?.commentCount ?? 0);
        });
        const html = eventTriangleHtml(total, tickKind(noteTotal, otherTotal));
        if (cluster.data && cluster.data.content !== html) {
            cluster.data.content = html;
            cluster.dirty = true;
            const el = cluster.dom?.content;
            if (el && el.innerHTML !== html) el.innerHTML = html;
        }
        // Collapsed clusters drive the interactive React popover (see itemover), so clear any native
        // title — otherwise vis would also show its own non-interactive popup.
        if (cluster.data) cluster.data.title = undefined;
    });
};

const makeOptions = (axisMonths: number, labelMonths: number[]): TimelineOptions => {
    // Pad both ends by half a month so an event triangle centered on month 0 (or the last month) isn't
    // half-clipped by the panel edge — this is about the triangle's own half-width, so a flat 0.5 is
    // enough. Without it, left-edge ticks like San Juan's award-date event lose their left half.
    const buffer = 0.5;
    return {
    stack: false,
    showCurrentTime: false,
    // Zoom is off: the dashboard is a fixed overview, so the window stays at the full range.
    // Overlapping ticks stay collapsed into count bubbles (fine here — the tooltip lists every event).
    zoomable: false,
    moveable: false,
    orientation: 'top',
    groupHeightMode: 'fixed',
    cluster: CLUSTER_OPTION,
    min: monthsToDate(-buffer),
    max: monthsToDate(axisMonths + buffer),
    start: monthsToDate(-buffer),
    end: monthsToDate(axisMonths + buffer),
    margin: { item: 4, axis: 6 },
    // Tick every month, but only label the planned phase boundaries. Per-month gridlines are hidden
    // via CSS so only those numbers show.
    timeAxis: { scale: 'month', step: 1 },
    format: {
        minorLabels: (date: any) => {
            const d = date && typeof date.toDate === 'function' ? date.toDate() : new Date(date);
            const m = monthsSinceEpoch(d);
            return labelMonths.includes(m) ? String(m) : '';
        },
        majorLabels: () => '',
    },
    xss: { disabled: true },
    // Match the legacy chart's 400px framing; keep vertical scroll so long project lists still fit.
    maxHeight: 400,
    verticalScroll: true,
    tooltip: { followMouse: true, overflowMethod: 'flip' },
    };
};

export default function DashboardTimeline() {
    const app = useAppContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<Timeline | null>(null);
    const itemsRef = useRef<DataSet<DataItem> | null>(null);
    // Raw payload kept so toggling the on-track filter rebuilds without refetching.
    const dataRef = useRef<DashboardV2Dto | null>(null);

    const [selectedFiscalYear, setSelectedFiscalYear] = useState<any>();
    const [fiscalYears, setFiscalYears] = useState<string[]>([]);
    const [loadIndicatorVisible, setLoadIndicatorVisible] = useState(false);
    const [hasData, setHasData] = useState(true);
    const [hideOnTrack, setHideOnTrack] = useState(false);

    // Interactive hover tooltip (clickable file downloads): vis's native title can't hold links.
    const [hoverTip, setHoverTip] = useState<HoverTip | null>(null);
    const tipCloseTimer = useRef<number | null>(null);
    const tipItemRef = useRef<string | number | null>(null);

    const fiscalYearsLabel = { 'aria-label': 'Fiscal Years' };

    const cancelTipClose = () => {
        if (tipCloseTimer.current !== null) {
            window.clearTimeout(tipCloseTimer.current);
            tipCloseTimer.current = null;
        }
    };
    const closeTip = () => {
        cancelTipClose();
        tipItemRef.current = null;
        setHoverTip(null);
    };
    // Keep the tooltip alive briefly after the pointer leaves the tick so the user can move into it
    // (and click a file). It closes immediately on the panel's own mouseleave.
    const scheduleTipClose = () => {
        cancelTipClose();
        tipCloseTimer.current = window.setTimeout(closeTip, 600);
    };
    const onTipEnter = () => cancelTipClose();
    const onTipLeave = () => closeTip();

    const downloadEventFile = async (fileId: string, fileName: string) => {
        app.showLoadPanel();
        const error = await downloadFileById(fileId, fileName, app.configuration?.streamingFileTransfer);
        app.hideLoadPanel();
        if (error) app.errorToast(error);
    };

    // Open an event's task/subtask on its grant. The destination is a different page, so it opens in
    // a new tab (ticket 1015) - the dashboard stays put behind it, matching the grant-label click
    // below. GrantDetails reads the hash on mount and expands/selects from there.
    const openEventLocation = (ev: TooltipEvent, includeSubtask: boolean) => {
        const route = buildDeepLinkRoute(
            ev.grantId ?? '',
            ev.moduleTypeId,
            ev.phaseId,
            includeSubtask ? ev.stepId : null,
        );
        if (route) window.open(route, '_blank');
    };

    useEffect(() => {
        const load = async () => {
            const years = await dashboardApi.getFiscalYears();
            setFiscalYears(years);
            if (years && years.length) {
                setSelectedFiscalYear(years[0]);
                populateData(years[0]);
            }
        };
        load();
        return () => {
            closeTip();
            timelineRef.current?.destroy();
            timelineRef.current = null;
            itemsRef.current = null;
        };
    }, []);

    const onFiscalYearChanged = (val: any) => {
        setSelectedFiscalYear(val.value);
        populateData(val.value);
    };

    const onHideOnTrackChanged = (e: any) => {
        const next = !!e.value;
        setHideOnTrack(next);
        renderFromData(next);
    };

    const populateData = async (fiscalYear: any) => {
        try {
            setLoadIndicatorVisible(true);
            const result = await getDashboardTimeline(String(fiscalYear));
            dataRef.current = result?.data ?? null;
            renderFromData(hideOnTrack);
        } catch (ex) {
            dataRef.current = null;
            renderFromData(hideOnTrack);
        } finally {
            setLoadIndicatorVisible(false);
        }
    };

    const renderFromData = (hide: boolean) => {
        const { groups, items, monthsToToday, axisMonths, labelMonths } = buildTimeline(dataRef.current, hide);
        setHasData(items.length > 0);
        renderTimeline(groups, items, monthsToToday, axisMonths, labelMonths);
    };

    const renderTimeline = (groups: DataGroup[], items: DataItem[], monthsToToday: number, axisMonths: number, labelMonths: number[]) => {
        if (!containerRef.current) return;
        // Rebuild from scratch on each change — the whole group and item set is replaced.
        closeTip();
        timelineRef.current?.destroy();

        const groupSet = new DataSet<DataGroup>(groups);
        const itemSet = new DataSet<DataItem>(items);
        const timeline = new Timeline(containerRef.current, itemSet, groupSet, makeOptions(axisMonths, labelMonths));
        timelineRef.current = timeline;
        itemsRef.current = itemSet;

        // After every redraw, rewrite collapsed cluster bubbles to show the total events they merged.
        timeline.on('changed', () => patchClusterCounts(timeline));

        // Clustering computes its scale from the measured panel width. On first paint that width can
        // still be 0 (container not laid out yet), so the cluster pass produces nothing until a later
        // rangechange re-measures it. Poll until the container has a real width, then redraw() to push
        // it into vis's domProps and nudge the window 1ms (the same rangechange a zoom fires) so the
        // clusters compute and paint on load rather than only after a manual zoom.
        let clusterTries = 0;
        const primeClusters = () => {
            if (timelineRef.current !== timeline || !containerRef.current) return;
            if (containerRef.current.clientWidth === 0) {
                if (++clusterTries < 120) requestAnimationFrame(primeClusters);
                return;
            }
            timeline.redraw();
            const { start, end } = timeline.getWindow();
            timeline.setWindow(start, new Date(end.getTime() - 1), { animation: false });
            timeline.setWindow(start, end, { animation: false });
            const itemSetRef = getItemSet(timeline);
            if (itemSetRef?.markDirty) {
                itemSetRef.markDirty({ refreshItems: true, restackGroups: true });
                timeline.redraw();
            }
            patchClusterCounts(timeline);
        };
        requestAnimationFrame(primeClusters);

        // Current-date line at the current month offset. It can't be dragged (pointer-events are off
        // in CSS). The date label is drawn by CSS from a data-today attribute, matching the Progress
        // Monitor's current-time line. vis adds the custom-time element a frame or two after we create
        // it, so an immediate query misses it; retry on animation frames until it exists, and bail if a
        // newer timeline has replaced this one.
        timeline.addCustomTime(monthsToDate(monthsToToday), TODAY_MARKER_ID);
        let stampTries = 0;
        const stampToday = () => {
            if (timelineRef.current !== timeline) return;
            const todayLine = containerRef.current?.querySelector(`.vis-custom-time.${TODAY_MARKER_ID}`) as HTMLElement | null;
            if (todayLine) {
                todayLine.setAttribute('data-today', moment().format('MMM D'));
                return;
            }
            if (stampTries++ < 30) requestAnimationFrame(stampToday);
        };
        stampToday();

        // Clicking a project's row label opens that grant in a new tab, like the legacy timeline.
        timeline.on('click', (props: any) => {
            if (props?.what === 'group-label' && props.group != null) {
                window.open('/grantdetails/' + props.group, '_blank');
            }
        });

        // Interactive tooltip: show our panel when hovering an event tick (it carries ptEvents). Bars
        // have a native title and no ptEvents, so they're ignored here and keep the native tooltip.
        timeline.on('itemover', (e: any) => {
            if (!itemsRef.current || !e?.item) return;
            // A single tick is a real DataSet item carrying ptEvents directly; a collapsed cluster
            // isn't in the DataSet, so pull it from the ItemSet and aggregate its merged day-ticks.
            let header: string | undefined;
            let days: HoverTipDay[] = [];
            let extraDays = 0;
            const single = itemsRef.current.get(e.item) as any;
            if (single?.ptEvents) {
                header = single.ptHeader;
                days = [{ dateStr: single.ptDateStr, events: single.ptEvents as TooltipEvent[] }];
            } else {
                const cluster = getItemSet(timeline)?.getItemById?.(e.item);
                const ticks: any[] = (cluster?.data?.uiItems ?? [])
                    .map((ui: any) => ui?.data)
                    .filter((d: any) => d?.ptEvents);
                if (ticks.length) {
                    header = ticks[0].ptHeader;
                    // Merge same-date ticks into one day each (newest first); the tooltip body scrolls.
                    days = mergeTicksToDays(ticks);
                }
            }
            if (header === undefined || !days.length) return;
            cancelTipClose();
            // Same marker already shown → don't re-anchor (avoids jitter while the pointer dwells).
            if (tipItemRef.current === e.item) return;
            const target = e.event?.target as HTMLElement | undefined;
            const markerEl = target?.closest('.vis-item') as HTMLElement | null;
            const rect = markerEl?.getBoundingClientRect();
            const cx = e.event?.clientX ?? 0;
            const cy = e.event?.clientY ?? 0;
            tipItemRef.current = e.item;
            setHoverTip({
                anchorLeft: rect ? rect.left : cx,
                anchorRight: rect ? rect.right : cx,
                anchorTop: rect ? rect.top : cy,
                anchorBottom: rect ? rect.bottom : cy,
                header,
                // 'modules' depth groups the tooltip by phase → step, so each event is labeled with
                // its phase (not just its step) — needed because a boundary cluster can span phases.
                depth: 'modules',
                days,
                extraDays,
            });
        });
        timeline.on('itemout', (e: any) => {
            const item = e?.item && itemsRef.current ? (itemsRef.current.get(e.item) as any) : null;
            if (item && !item.ptEvents) return; // leaving a bar shouldn't close an open tip
            scheduleTipClose();
        });
    };

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={loadIndicatorVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                container="#dashboard_container"
            />
            <style>{`
                .db-timeline .vis-item.db-bar { overflow: visible !important; }
                .db-timeline .vis-item.db-bar .vis-item-overflow { overflow: visible !important; }
                .db-timeline .vis-item.db-bar .vis-item-content { white-space: nowrap; }
                /* Stub (sub-month) bar: put the phase label just past the sliver's right edge on the
                   white area, like the legacy chart, instead of overlapping the thin bar. */
                .db-timeline .vis-item.db-bar-stub .vis-item-content {
                    left: 100% !important;
                    padding-left: 3px !important;
                    color: #000;
                }
                .db-timeline .vis-labelset .vis-label { cursor: pointer; }
                .db-timeline .vis-labelset .vis-label .vis-inner { padding: 6px 8px !important; white-space: nowrap; }
                /* Institution row label: truncate with ellipsis, full name on hover (matches legacy). */
                .db-timeline .db-rowlabel {
                    display: inline-block;
                    max-width: 260px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    vertical-align: middle;
                }
                /* Hide the per-month gridlines so only the DC phase-interval labels show, like legacy. */
                .db-timeline .vis-time-axis .vis-grid.vis-minor,
                .db-timeline .vis-time-axis .vis-grid.vis-major { border-width: 0; }
                /* Event ticks are 'box' items; hide the box's dot and vertical line so only the
                   triangle shows, and make the item itself transparent (matches Progress Monitor). */
                .db-timeline .vis-item.db-event-tick {
                    background: transparent !important;
                    border: 0 !important;
                }
                .db-timeline .vis-item.db-event-tick .vis-item-content { padding: 0 !important; }
                .db-timeline .vis-item.db-event-tick .vis-dot,
                .db-timeline .vis-item.db-event-tick .vis-line { display: none !important; }
                /* A vis cluster (overlapping ticks collapsed at a zoomed-out scale) renders the same
                   triangle as a single tick, so make the cluster box itself transparent too. */
                .db-timeline .vis-item.vis-cluster {
                    background: transparent !important;
                    border: 0 !important;
                    box-shadow: none !important;
                }
                .db-timeline .vis-item.vis-cluster .vis-item-content { padding: 0 !important; }
                .db-timeline .vis-item.vis-cluster .vis-dot,
                .db-timeline .vis-item.vis-cluster .vis-line { display: none !important; }
                /* Inverted-triangle event marker (shared markup with the Progress Monitor). */
                .db-timeline .pt-tick-tri {
                    display: inline-block;
                    line-height: 0;
                    transform: translateY(-3px);
                }
                .db-timeline .pt-tick-tri svg { overflow: visible; display: block; }
                .db-timeline .pt-tick-tri polygon {
                    fill: #2196F3;
                    stroke: #2196F3;
                    stroke-width: 1;
                }
                .db-timeline .pt-tick-tri.pt-tick-note polygon { fill: #0D47A1; stroke: #0D47A1; }
                .db-timeline .pt-tick-tri.pt-tick-both polygon { fill: #2196F3; stroke: #0D47A1; stroke-width: 3; }
                .db-timeline .pt-tick-tri:hover polygon { stroke: #000; }
                .db-timeline .pt-tick-tri .pt-tri-count {
                    fill: #fff;
                    font-family: Arial, sans-serif;
                    font-size: 9px;
                    font-weight: 700;
                    pointer-events: none;
                }
                /* Bar/native tooltip look, matching the DevExtreme dx.light surface. */
                .db-timeline .vis-tooltip {
                    background-color: #fff !important;
                    color: #333 !important;
                    border: 1px solid #ddd !important;
                    border-radius: 4px !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
                    padding: 8px 12px !important;
                    font-family: "Helvetica Neue","Segoe UI",helvetica,verdana,sans-serif !important;
                    font-size: 14px !important;
                    line-height: 1.35715 !important;
                    color-scheme: light;
                    max-width: 320px !important;
                    white-space: normal !important;
                    pointer-events: none;
                }
                /* Current-date line, matching the Progress Monitor: an orange bar starting below a
                   small date pill. Non-draggable (pointer-events off). */
                .db-timeline .vis-custom-time.${TODAY_MARKER_ID} {
                    background: linear-gradient(to bottom, transparent 0, transparent 32px, #ff5722 32px) !important;
                    pointer-events: none;
                }
                .db-timeline .vis-custom-time.${TODAY_MARKER_ID}::before {
                    content: attr(data-today);
                    position: absolute;
                    top: 14px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #ff5722;
                    color: #fff;
                    padding: 1px 6px;
                    font-size: 10px;
                    font-weight: 600;
                    line-height: 1.4;
                    border-radius: 3px;
                    white-space: nowrap;
                    pointer-events: none;
                    z-index: 2;
                }
                .db-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 24px;
                    flex-wrap: wrap;
                }
            `}</style>
            <div className="container-fluid panelBody" style={{ minHeight: '538px' }} id="dashboard_container">
                <div className="row grantVisualizationRow">
                    <div className="col-12">
                        <br />
                        <div className="db-toolbar">
                            <div style={{ minWidth: 220 }}>
                                <SelectBox
                                    items={fiscalYears}
                                    inputAttr={fiscalYearsLabel}
                                    value={selectedFiscalYear}
                                    onValueChanged={onFiscalYearChanged}
                                />
                            </div>
                            <CheckBox
                                text="Hide on-track projects"
                                value={hideOnTrack}
                                onValueChanged={onHideOnTrackChanged}
                                hint="Hide projects whose phases are all complete (green)."
                            />
                        </div>
                        <br />
                        {!hasData && !loadIndicatorVisible && (
                            <div style={{ padding: '12px 4px', color: '#666', fontStyle: 'italic' }}>
                                No projects with phase data for this selection.
                            </div>
                        )}
                        <div className="db-timeline" ref={containerRef} style={{ width: '100%' }} />
                        <StatusLegend exclude={['Scheduled', 'Projected', 'Request', 'Revision Request', 'Response']} />
                    </div>
                </div>
            </div>
            <EventTooltip
                hoverTip={hoverTip}
                onTipEnter={onTipEnter}
                onTipLeave={onTipLeave}
                downloadEventFile={downloadEventFile}
                openEventLocation={openEventLocation}
            />
        </>
    );
}
